import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";

const VALUES = [
    {
        n: "01",
        t: "Clarity over cleverness",
        d: "Financial decisions aren't solved by bigger models — they're solved by clearer data. We engineer for obviousness, not novelty.",
    },
    {
        n: "02",
        t: "Regional-first, not US-first",
        d: "Our bank support starts where fintech has the hardest edges: Jakarta, Manila, Ho Chi Minh City. Everything else follows.",
    },
    {
        n: "03",
        t: "Boring is a feature",
        d: "We ship typed SDKs, versioned responses, dull docs. Lenders run our output straight into policy engines — no surprises allowed.",
    },
    {
        n: "04",
        t: "Under-promise. Over-land.",
        d: "If a bank isn't on the coverage list, we haven't processed 10k statements with it yet. Coverage claims are audited monthly.",
    },
];

const TIMELINE = [
    { y: "2022", t: "Prototype", d: "Built in a Singapore co-working space after watching a lender reject a worthy SME over a scanned BCA PDF." },
    { y: "2023", t: "Bank #1", d: "First production customer — a digital lender processing 12k statements/month." },
    { y: "2024", t: "Series A", d: "Led by Sequoia India with participation from regional angels. 48 banks live by year-end." },
    { y: "2025", t: "Regional", d: "Expanded to all five ASEAN markets. Crossed 220 banks, launched Credit Bureau Analysis private beta." },
    { y: "2026", t: "Today", d: "Building the underwriting workspace — one place for every signal about a borrower." },
];

const TEAM = [
    { n: "Ayu Pradnyadari", r: "CEO & co-founder", city: "Singapore", i: "AP" },
    { n: "Rizki Wardana", r: "CTO & co-founder", city: "Jakarta", i: "RW" },
    { n: "Ngoc Pham Trang", r: "Head of ML", city: "Ho Chi Minh City", i: "NP" },
    { n: "Sarah Chen Li", r: "Head of Revenue", city: "Kuala Lumpur", i: "SC" },
    { n: "Marco Dela Cruz", r: "Head of Risk Partnerships", city: "Manila", i: "MD" },
    { n: "Lim Wei Jian", r: "Head of Engineering", city: "Singapore", i: "LW" },
];

export default function About() {
    return (
        <div data-testid="page-about">
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-hero pointer-events-none" />
                <div className="absolute inset-0 bg-grid-subtle bg-grid-fade opacity-55 pointer-events-none" />
                <div className="relative container max-w-[1240px] mx-auto px-6 pt-20 pb-16 md:pt-28 md:pb-24">
                    <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">About CredNX</p>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-5 font-serif text-[48px] sm:text-[64px] lg:text-[82px] leading-[0.98] tracking-[-0.035em] text-balance max-w-[1000px]"
                    >
                        We build the{" "}
                        <span className="italic">unglamorous infrastructure</span>{" "}
                        that makes fair lending possible.
                    </motion.h1>
                    <p className="mt-8 text-[17px] sm:text-[19px] leading-[1.6] text-muted-foreground max-w-[700px]">
                        Southeast Asia&apos;s credit story is hidden inside three hundred million bank statements that nobody can read cleanly. CredNX decodes them — so lenders can say yes to the borrowers who deserve it.
                    </p>
                </div>
            </section>

            {/* Split manifesto */}
            <section className="relative">
                <div className="container max-w-[1240px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground mb-6">Why we exist</p>
                        <h2 className="font-serif text-[32px] md:text-[44px] leading-[1.05] tracking-tight text-balance">
                            A statement is not{" "}
                            <span className="italic">just paper.</span>{" "}
                            It&apos;s a resume for capital.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-[15.5px] leading-[1.75] text-foreground/85">
                        <p>
                            In Southeast Asia, millions of small businesses and individuals live entirely on cash-flow. They pay their suppliers, their staff, their landlords through a single bank account — and that account tells a truer story about their creditworthiness than any bureau ever could.
                        </p>
                        <p>
                            But the data is locked. PDFs from <em className="not-italic font-medium text-foreground">two hundred and twenty banks</em>, in six languages, across five regulatory regimes. No two formats alike. So lenders either ignore it, or hire teams of humans to transcribe it. Both are broken.
                        </p>
                        <p>
                            We built CredNX because the alternative — fewer people getting fair access to credit — isn&apos;t acceptable. Every parser we ship puts a real borrower in front of a real yes.
                        </p>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="relative bg-background-soft/70 border-y border-border">
                <div className="container max-w-[1240px] mx-auto px-6 py-24 md:py-32">
                    <div className="max-w-[680px] mb-14">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Principles</p>
                        <h2 className="mt-4 font-serif text-[38px] md:text-[54px] leading-[1.02] tracking-tight text-balance">
                            Four things we believe,{" "}
                            <span className="italic">deeply.</span>
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                        {VALUES.map((v, i) => (
                            <motion.div
                                key={v.n}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                                className="rounded-2xl bg-card border border-border p-8 hover:border-primary/30 transition-colors"
                            >
                                <div className="flex items-baseline gap-4 mb-5">
                                    <span className="font-mono text-[12px] text-muted-foreground">{v.n}</span>
                                    <div className="flex-1 h-px bg-border" />
                                </div>
                                <h3 className="font-serif text-[26px] tracking-tight leading-tight">{v.t}</h3>
                                <p className="mt-3 text-[14.5px] text-muted-foreground leading-relaxed">{v.d}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline */}
            <section className="relative">
                <div className="container max-w-[1240px] mx-auto px-6 py-24 md:py-32">
                    <div className="max-w-[680px] mb-14">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Chronology</p>
                        <h2 className="mt-4 font-serif text-[38px] md:text-[54px] leading-[1.02] tracking-tight text-balance">
                            A short history.
                        </h2>
                    </div>

                    <ol className="relative border-l border-border-strong/60 ml-3">
                        {TIMELINE.map((e, i) => (
                            <motion.li
                                key={e.y}
                                initial={{ opacity: 0, x: -12 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, amount: 0.4 }}
                                transition={{ duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                                className="pl-8 pb-12 relative last:pb-0"
                            >
                                <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-accent border-4 border-background" />
                                <div className="flex items-baseline gap-4">
                                    <p className="font-mono text-[12.5px] text-muted-foreground tracking-wider">{e.y}</p>
                                    <div className="h-px flex-1 bg-border" />
                                </div>
                                <h3 className="mt-2 font-serif text-[26px] tracking-tight">{e.t}</h3>
                                <p className="mt-1.5 text-[14.5px] text-muted-foreground leading-relaxed max-w-[600px]">{e.d}</p>
                            </motion.li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* Team */}
            <section className="relative bg-surface-deep text-surface-deep-fg overflow-hidden">
                <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
                <div className="absolute -top-40 -left-20 w-[440px] h-[440px] rounded-full bg-accent/10 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-40 -right-20 w-[440px] h-[440px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />

                <div className="relative container max-w-[1240px] mx-auto px-6 py-24 md:py-32">
                    <div className="max-w-[680px] mb-14">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-accent/90">Team</p>
                        <h2 className="mt-4 font-serif text-[38px] md:text-[54px] leading-[1.02] tracking-tight text-balance">
                            Built across the region,{" "}
                            <span className="italic text-surface-deep-fg/75">for the region.</span>
                        </h2>
                    </div>

                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
                        {TEAM.map((p, i) => (
                            <motion.div
                                key={p.n}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.6, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                                className="rounded-2xl border border-surface-deep-fg/10 bg-surface-deep-fg/[0.03] p-6 hover:bg-surface-deep-fg/[0.06] transition-colors"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-gradient-gold inline-flex items-center justify-center text-[14px] font-serif font-semibold text-gold-foreground">
                                        {p.i}
                                    </div>
                                    <div>
                                        <p className="text-[15px] font-medium">{p.n}</p>
                                        <p className="text-[12.5px] text-surface-deep-fg/60">{p.r}</p>
                                    </div>
                                </div>
                                <div className="mt-5 inline-flex items-center gap-1.5 text-[12px] text-surface-deep-fg/55">
                                    <MapPin className="w-3.5 h-3.5" /> {p.city}
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-14 flex flex-wrap items-center gap-4">
                        <Button variant="lime" size="lg" asChild>
                            <Link to="/contact">
                                Work with us <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </Button>
                        <span className="inline-flex items-center gap-2 text-[13px] text-surface-deep-fg/60">
                            <Flag className="w-4 h-4 text-accent" /> 14 open roles across engineering, ML, and revenue
                        </span>
                    </div>
                </div>
            </section>
        </div>
    );
}
